import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Sparkles, ChevronDown, Smartphone, Menu, X } from "lucide-react";

export function LandingNavigation({ navigate, handleNearbyWaters }) {
  const [exploreOpen, setExploreOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const exploreRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exploreRef.current && !exploreRef.current.contains(event.target)) setExploreOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeMenuAndNavigate = (path) => {
    setMobileMenuOpen(false);
    setExploreOpen(false);
    navigate(path);
  };

  const handleNearbyClick = () => {
    handleNearbyWaters();
    setExploreOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-b from-black/50 to-transparent" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="font-serif text-lg sm:text-xl text-white font-medium cursor-pointer" onClick={() => navigate('/')}>
            DIY Fly Fishing Guide
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <div className="relative" ref={exploreRef}>
              <button
                onClick={() => setExploreOpen(!exploreOpen)}
                className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="landing-explore-btn"
              >
                Explore
                <ChevronDown className={`w-4 h-4 transition-transform ${exploreOpen ? 'rotate-180' : ''}`} />
              </button>
              {exploreOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-background rounded-xl shadow-xl border border-border overflow-hidden z-50">
                  <div className="py-2">
                    <button onClick={handleNearbyClick} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left" data-testid="landing-nearby-waters">
                      <MapPin className="w-5 h-5 text-accent" />
                      <div><p className="font-medium text-foreground text-sm">Nearby Waters</p><p className="text-xs text-muted-foreground">Find fishing spots near you</p></div>
                    </button>
                    <button onClick={() => closeMenuAndNavigate('/ai-guide')} className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left" data-testid="landing-ai-guide">
                      <Sparkles className="w-5 h-5 text-accent" />
                      <div><p className="font-medium text-foreground text-sm">Trip Planner</p><p className="text-xs text-muted-foreground">Get personalized recommendations</p></div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <Button size="sm" className="ml-1 rounded-full bg-white text-primary hover:bg-white/90" onClick={() => navigate("/app")} data-testid="landing-get-app">
              <Smartphone className="w-4 h-4 mr-2" />Get the app
            </Button>
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-md text-white/80 hover:text-white hover:bg-white/10 transition-colors" data-testid="landing-mobile-menu-btn">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="fixed inset-0 bg-black/50 md:hidden mobile-nav-overlay" onClick={() => setMobileMenuOpen(false)} />}

      {/* Mobile Menu Panel */}
      <div className={`fixed top-0 right-0 h-screen w-80 max-w-[85vw] bg-background shadow-2xl transform transition-transform duration-300 md:hidden mobile-nav-panel ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} data-testid="landing-mobile-panel">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <span className="font-serif text-lg font-medium">Menu</span>
            <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-md hover:bg-muted transition-colors" data-testid="landing-mobile-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto py-4 px-4 space-y-1">
            <button onClick={handleNearbyClick} className="w-full flex items-center gap-3 py-3 text-foreground transition-colors" data-testid="landing-mobile-nearby"><MapPin className="w-5 h-5 text-accent" />Nearby Waters</button>
            <button onClick={() => closeMenuAndNavigate('/ai-guide')} className="w-full flex items-center gap-3 py-3 text-foreground transition-colors" data-testid="landing-mobile-ai-guide"><Sparkles className="w-5 h-5 text-accent" />Trip Planner</button>
            <div className="border-t border-border my-3" />
            <button onClick={() => closeMenuAndNavigate('/app')} className="w-full flex items-center gap-3 py-3 text-accent font-medium" data-testid="landing-mobile-get-app"><Smartphone className="w-5 h-5 text-accent" />Get the app</button>
          </div>
        </div>
      </div>
    </>
  );
}
